/**
 * useAuth Hook
 *
 * Custom React hook for managing authentication state.
 * Handles OAuth callbacks, user session, and authentication status.
 */

import { useState, useEffect } from 'react';
import { getCurrentUser, logout, handleAuthCallback } from '../services/api';
import type { User } from '../services/api/types';

export interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  logout: () => void;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing authentication state
 *
 * Automatically handles OAuth callbacks and fetches current user on mount.
 * Provides authentication status and user information.
 *
 * @returns Authentication state and methods
 *
 * @example
 * ```tsx
 * function App() {
 *   const { user, loading, isAuthenticated, isAdmin, logout } = useAuth();
 *
 *   if (loading) return <div>Loading...</div>;
 *
 *   if (!isAuthenticated) {
 *     return <LoginPage />;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>Welcome, {user?.name}</h1>
 *       {isAdmin && <AdminPanel />}
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = async () => {
    try {
      setLoading(true);
      setError(null);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      // Not authenticated or error
      setError(err instanceof Error ? err.message : 'Authentication error');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle OAuth callback (extract token from URL)
    handleAuthCallback();

    // Try to get current user
    checkAuth();
  }, []);

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    logout: handleLogout,
    refetch: checkAuth,
  };
}
